import AsyncStorage from '@/utils/async-storage-chunker';
import { captureException } from '@/utils/sentry';
import {
  defaultDynamicConfigValues,
  getValueFromConfigs,
} from '@/contexts/dynamic-config-utils';
import {
  EmployeeRestaurant,
  useAuthenticatedUserContext,
} from '@/contexts/user-context';
import { OFFLINE_DATA_KEY_PREFIX, OfflineSyncType } from '@/utils/offline-mode';
import {
  OfflineBackgroundSyncRegistryAdapter,
  OfflineBatchTagPrintingAdapter,
  OfflineCreditRequestDataAdapter,
  OfflineDataStatus,
  OfflineDataStatusAdapter,
  OfflineMinimalVariantPackAdapter,
  OfflineOrderConstraintsAdapter,
  OfflineOrderContextAdapter,
  OfflineOrderGuideAdapter,
  OfflineOrderGuidePriceEditDisplayInfoAdapter,
  OfflineQueuedOrdersAdapter,
  OfflineVariantPackInfoAdapter,
  OfflineVisibleVariantPackAdapter,
  OfflineVisibleVariantPackIDAdapter,
} from '@/adapters/offline-mode';
import { PermissionAction, PermissionSubject } from '@/enums';
import { useDownloadCreditRequestData } from './offline-mode/use-download-credit-request-data';
import { useDownloadMinimalVariantPacks } from './offline-mode/use-download-minimal-variant-packs';
import { useDownloadOrderConstraints } from './offline-mode/use-download-order-constraints';
import { useDownloadOrderContext } from './offline-mode/use-download-order-context';
import { useDownloadOrderGuide } from './offline-mode/use-download-order-guide';
import { useDownloadPriceEditDisplayInfo } from './offline-mode/use-download-price-edit-display-info';
import { useDownloadTagPrintBatchQueries } from './offline-mode/use-download-tag-print-batch-queries';
import { useDownloadUserContext } from './offline-mode/use-download-user-context';
import { useDownloadVariantPackInfo } from './offline-mode/use-download-variant-pack-info';
import { useDownloadVisibleVariantPacks } from './offline-mode/use-download-visible-variant-packs';
import { useState } from 'react';

interface UseDataHookState {
  downloadEligibleOfflineData: (
    relationship: EmployeeRestaurant,
    supplierUUID: string,
  ) => Promise<OfflineDataStatus | null>;
  downloadOfflineDataTypes: (
    relationship: EmployeeRestaurant,
    supplierUUID: string,
    eligibleSyncTypes: OfflineSyncType[],
  ) => Promise<OfflineDataStatus | null>;
  downloadStatusMap: DownloadStatusMap;
  getEnabledSyncTypes: (relationship: EmployeeRestaurant) => OfflineSyncType[];
  removeAllOfflineData: () => Promise<void>;
  removeOfflineData: (
    restaurantUUID: string,
    supplierUUID: string,
  ) => Promise<void>;
  updateRestaurantDataStatus: (
    restaurantUUID: string,
    status: OfflineDataStatus,
  ) => Promise<void>;
}

export interface DownloadStatusMap {
  [chatUUID: string]: {
    error?: boolean;
    isDownloading: boolean;
  };
}

/**
 * Orchestrates downloading offline data for a specific restaurant.
 *
 * This hook manages the download process by calling composing calls to various download
 * functions and updating the status map.
 *
 * @returns {Object} - An object containing the downloadOfflineData function and the download status map.
 *
 * @function
 * @name useOfflineDownload
 *
 * @example
 * const { downloadEligibleOfflineData, downloadStatusMap } = useOfflineDownload();
 *
 * // Download all offline data for a specific restaurant and supplier
 * downloadAllOfflineData(relationship, 'supplier-uuid');
 *
 * // Download specific sync types for a specific restaurant and supplier
 * downloadOfflineDataTypes(relationship, 'supplier-uuid', [OfflineSyncType.UserContext]);
 */
export const useOfflineDownload = (): UseDataHookState => {
  const { employeeCan, supplierDynamicConfigMap } =
    useAuthenticatedUserContext();
  const [hasDownloadedUserContext, setHasDownloadedUserContext] =
    useState(false);
  const [downloadStatusMap, setDownloadStatusMap] = useState<DownloadStatusMap>(
    {},
  );

  const { downloadUserContext } = useDownloadUserContext();
  const { downloadOrderConstraints } = useDownloadOrderConstraints();
  const { downloadOrderContext } = useDownloadOrderContext();
  const { downloadOrderGuide } = useDownloadOrderGuide();
  const { downloadPriceEditDisplayInfo } = useDownloadPriceEditDisplayInfo();
  const { downloadVariantPackInfo } = useDownloadVariantPackInfo();
  const { downloadTagBatchPrintData } = useDownloadTagPrintBatchQueries();
  const { downloadVisibleVariantPacks } = useDownloadVisibleVariantPacks();
  const { downloadMinimalVariantPacks, downloadVisibleVariantPackIDs } =
    useDownloadMinimalVariantPacks();
  const { downloadCreditRequestData } = useDownloadCreditRequestData();

  /**
   * Returns the sync types that are enabled to be downloaded for a given restaurant.
   *
   * @param {EmployeeRestaurant} relationship - The relationship object containing restaurant details.
   * @returns {OfflineSyncType[]} - An array of sync types that are enabled to be downloaded for the given restaurant.
   */
  const getEnabledSyncTypes = (
    relationship: EmployeeRestaurant,
  ): OfflineSyncType[] => {
    const baseSyncTypes = [
      OfflineSyncType.UserContext,
      OfflineSyncType.OrderContext,
      OfflineSyncType.OrderGuide,
      OfflineSyncType.VariantPackInfo,
    ];

    const offlineModeConfig = getValueFromConfigs(
      'offline_mode_config',
      supplierDynamicConfigMap[relationship.supplierUUID],
      relationship.chatDynamicConfig,
      defaultDynamicConfigValues.offline_mode_config,
    );
    const orderQuantityConfig = getValueFromConfigs(
      'order_quantity_config',
      supplierDynamicConfigMap[relationship.supplierUUID],
      relationship.chatDynamicConfig,
      defaultDynamicConfigValues.order_quantity_config,
    );
    const batchTagPrintingConfig = getValueFromConfigs(
      'tag_printing_config',
      supplierDynamicConfigMap[relationship.supplierUUID],
      relationship.chatDynamicConfig,
      defaultDynamicConfigValues.tag_printing_config,
    );
    const onDeviceScanningConfig = getValueFromConfigs(
      'on_device_scanning_config',
      supplierDynamicConfigMap[relationship.supplierUUID],
      undefined, // local scan enablement only on supplier-level
      defaultDynamicConfigValues.on_device_scanning_config,
    );

    if (offlineModeConfig?.catalog_enabled) {
      baseSyncTypes.push(OfflineSyncType.VisibleVariantPacks);
      if (orderQuantityConfig?.min_max_quantity_v2_enabled) {
        baseSyncTypes.push(OfflineSyncType.OrderConstraints);
      }
      if (batchTagPrintingConfig?.batch_tag_printing_enabled) {
        baseSyncTypes.push(OfflineSyncType.BatchTagPrinting);
      }
    }

    // Don't download price edit config data for chats that don't have
    // SRT enabled, otherwise we'll receive a "FORBIDDEN" error
    if (
      relationship.salesRepModeEnabled &&
      employeeCan(PermissionAction.Read, PermissionSubject.SrtPriceEditing)
    ) {
      baseSyncTypes.push(OfflineSyncType.PriceEditDisplayInfo);
    }

    if (relationship.showCreditRequest) {
      baseSyncTypes.push(OfflineSyncType.CreditRequest);
    }

    if (onDeviceScanningConfig?.local_scan_ux_enabled) {
      baseSyncTypes.push(OfflineSyncType.MinimalVariantPack);
    }

    return baseSyncTypes;
  };

  /**
   * Downloads only specific eligible sync types for a given restaurant and supplier.
   * Note: This hook requires the caller to pass only enabled sync types through getEnabledSyncTypes.
   *
   * @async
   * @param {EmployeeRestaurant} relationship - The relationship object containing restaurant details.
   * @param {string} supplierUUID - The UUID of the supplier.
   * @param {OfflineSyncType[]} eligibleSyncTypes - Array of sync types to potentially download.
   * @returns {OfflineDataStatus | null} - The data status for the restaurant after downloading the eligible sync types.
   */
  const downloadOfflineDataTypes = async (
    relationship: EmployeeRestaurant,
    supplierUUID: string,
    eligibleSyncTypes: OfflineSyncType[],
  ): Promise<OfflineDataStatus | null> => {
    const restaurantUUID = relationship.uuid;
    const chatUUID = relationship.chatUUIDs[0];

    setDownloadStatusMap((statusMap) => ({
      ...statusMap,
      [restaurantUUID]: {
        isDownloading: true,
      },
    }));

    try {
      const downloadedSyncTypes: OfflineSyncType[] = [];

      for (const syncType of eligibleSyncTypes) {
        switch (syncType) {
          case OfflineSyncType.UserContext:
            // When downloading multiple chats in a row, we only need to download the user context once.
            // But if the user comes back later to download more, we should re-download the user context.
            // (Where "later" is defined by closing and re-opening the location picker).
            if (!hasDownloadedUserContext) {
              await downloadUserContext();
              setHasDownloadedUserContext(true);
            }

            // Always mark as downloaded after initial download
            downloadedSyncTypes.push(syncType);
            break;

          case OfflineSyncType.OrderConstraints:
            await downloadOrderConstraints(restaurantUUID, supplierUUID);
            downloadedSyncTypes.push(syncType);
            break;

          case OfflineSyncType.OrderContext:
            await downloadOrderContext(restaurantUUID, supplierUUID);
            downloadedSyncTypes.push(syncType);
            break;

          case OfflineSyncType.OrderGuide:
            await downloadOrderGuide(restaurantUUID, chatUUID, supplierUUID);
            downloadedSyncTypes.push(syncType);
            break;

          case OfflineSyncType.VariantPackInfo:
            const offlineModeConfig = getValueFromConfigs(
              'offline_mode_config',
              supplierDynamicConfigMap[relationship.supplierUUID],
              relationship.chatDynamicConfig,
              defaultDynamicConfigValues.offline_mode_config,
            );
            const orderGuideOnly = !offlineModeConfig?.catalog_enabled;

            await downloadVariantPackInfo(
              restaurantUUID,
              supplierUUID,
              orderGuideOnly,
            );
            downloadedSyncTypes.push(syncType);
            break;

          case OfflineSyncType.VisibleVariantPacks:
            await downloadVisibleVariantPacks(restaurantUUID, supplierUUID);
            downloadedSyncTypes.push(syncType);
            break;

          case OfflineSyncType.PriceEditDisplayInfo:
            await downloadPriceEditDisplayInfo(restaurantUUID, supplierUUID);
            downloadedSyncTypes.push(syncType);
            break;

          case OfflineSyncType.BatchTagPrinting:
            await downloadTagBatchPrintData(restaurantUUID, supplierUUID);
            downloadedSyncTypes.push(syncType);
            break;

          case OfflineSyncType.CreditRequest:
            await downloadCreditRequestData(restaurantUUID, supplierUUID);
            downloadedSyncTypes.push(syncType);
            break;

          case OfflineSyncType.MinimalVariantPack:
            await Promise.all([
              downloadMinimalVariantPacks(supplierUUID),
              downloadVisibleVariantPackIDs(restaurantUUID, supplierUUID),
            ]);
            downloadedSyncTypes.push(syncType);
            break;

          default: {
            const _exhaustiveCheck: never = syncType;
            throw new Error(`Unknown sync type: ${_exhaustiveCheck}`);
          }
        }
      }

      const currentTime = new Date().toISOString();
      const updatedRestaurantStatus =
        downloadedSyncTypes.reduce<OfflineDataStatus>(
          (acc, syncType) => ({
            ...acc,
            [syncType]: { downloadedDate: currentTime },
          }),
          {},
        );

      setDownloadStatusMap((statusMap) => ({
        ...statusMap,
        [restaurantUUID]: {
          isDownloading: false,
        },
      }));

      return updatedRestaurantStatus;
    } catch (e) {
      captureException(e);
      setDownloadStatusMap((statusMap) => ({
        ...statusMap,
        [restaurantUUID]: {
          error: true,
          isDownloading: false,
        },
      }));

      return null;
    }
  };
  /**
   * Downloads all eligible offline data for a given restaurant and supplier.
   * This is a convenience method that downloads all available sync types.
   *
   * @async
   * @param {EmployeeRestaurant} relationship - The relationship object containing restaurant details.
   * @param {string} supplierUUID - The UUID of the supplier.
   * @returns {OfflineDataStatus | null} - The data status for the restaurant after downloading the eligible sync types.
   */
  const downloadEligibleOfflineData = async (
    relationship: EmployeeRestaurant,
    supplierUUID: string,
  ): Promise<OfflineDataStatus | null> => {
    const eligibleSyncTypes = getEnabledSyncTypes(relationship);

    return downloadOfflineDataTypes(
      relationship,
      supplierUUID,
      eligibleSyncTypes,
    );
  };

  /**
   * Updates the data status for a specific restaurant after downloading.
   * This step is intentionally separate from the download operation since downloading and updating
   * data status for individual locations in parallel can cause individual data status updates to overwrite each other.
   * @param restaurantUUID
   * @param status
   */
  const updateRestaurantDataStatus = async (
    restaurantUUID: string,
    status: OfflineDataStatus,
  ) => {
    const dataStatusAdapter = new OfflineDataStatusAdapter();
    const dataStatus = await dataStatusAdapter.fetch();
    await dataStatusAdapter.save({
      ...dataStatus,
      [restaurantUUID]: {
        ...(dataStatus?.[restaurantUUID] || {}),
        ...status,
      },
    });
  };

  /**
   * Removes all offline data
   * @returns void
   */
  const removeAllOfflineData = async () => {
    const keys = await AsyncStorage.getAllKeys();
    const pepperKeys = keys.filter((key) =>
      key.startsWith(`${OFFLINE_DATA_KEY_PREFIX}:`),
    );
    await AsyncStorage.multiRemove(pepperKeys);
  };

  /**
   * Removes all offline data for a specific restaurant.
   * @param restaurantUUID - The UUID of the restaurant
   * @param supplierUUID - The UUID of the supplier
   */
  const removeOfflineData = async (
    restaurantUUID: string,
    supplierUUID: string,
  ) => {
    try {
      const adapters = [
        OfflineOrderContextAdapter.getInstance(restaurantUUID, supplierUUID),
        new OfflineOrderGuideAdapter(restaurantUUID, supplierUUID),
        new OfflineOrderGuidePriceEditDisplayInfoAdapter(
          restaurantUUID,
          supplierUUID,
        ),
        new OfflineOrderConstraintsAdapter(restaurantUUID, supplierUUID),
        new OfflineQueuedOrdersAdapter(restaurantUUID, supplierUUID),
        OfflineVariantPackInfoAdapter.getInstance(restaurantUUID, supplierUUID),
        new OfflineVisibleVariantPackAdapter(restaurantUUID, supplierUUID),
        OfflineBatchTagPrintingAdapter.getInstance(
          restaurantUUID,
          supplierUUID,
        ),
        new OfflineCreditRequestDataAdapter(restaurantUUID, supplierUUID),
        OfflineVisibleVariantPackIDAdapter.getInstance(
          restaurantUUID,
          supplierUUID,
        ),
        OfflineMinimalVariantPackAdapter.getInstance(supplierUUID),
      ];

      await Promise.all(adapters.map((adapter) => adapter.remove()));

      const dataStatusAdapter = new OfflineDataStatusAdapter();
      const dataStatus = await dataStatusAdapter.fetch();

      if (!dataStatus) {
        return;
      }

      delete dataStatus[restaurantUUID];
      await dataStatusAdapter.save(dataStatus);

      const backgroundSyncAdapter = new OfflineBackgroundSyncRegistryAdapter();
      await backgroundSyncAdapter.removeSyncMetadata(
        restaurantUUID,
        supplierUUID,
      );
    } catch (error) {
      captureException(error);
    }
  };

  return {
    downloadEligibleOfflineData,
    downloadOfflineDataTypes,
    downloadStatusMap,
    getEnabledSyncTypes,
    removeAllOfflineData,
    removeOfflineData,
    updateRestaurantDataStatus,
  };
};
